import { BrowserModule } from '@angular/platform-browser';
import { inject, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
// import { MatDialogModule } from '@angular/material/dialog';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as Sentry from '@sentry/browser';

import { SharedModule } from './shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { EnvironmentService } from './shared/environment.service';
import { ElectronEnvironmentService } from './environments/electron/electron-environment.service';
import { WebEnvironmentService } from './environments/web/web-environment.service';
import { environment } from '../environments/environment';
import { NavbarModule } from './shared/components/navbar/navbar.module';
import { MAT_RADIO_DEFAULT_OPTIONS, MatRadioDefaultOptions } from '@angular/material/radio';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { OnsiteCheckInClient } from "@azavista/planner-lib";
import { AuthPersistenceImpl } from "@azavista/planner-lib/lib/auth/auth-persistence";
import { AuthServiceImpl } from "@azavista/planner-lib/lib/auth/auth-service-impl";
import { createHttpClient } from '@azavista/planner-lib/lib/core/http';
import { Axios, AxiosError, AxiosInstance } from 'axios';
import { ConfigurationStorageService } from './shared/configuration-storage.service';
import { axiosErrorHandler, OnsiteCheckInClientErrorService } from './shared/components/onsite-checkin-client/onsite-checkin-client-error.service';
import { CustomerChatbotService } from './shared/components/customer-chatbot/types';
import { IntercomCustomerChatbotService } from './shared/components/customer-chatbot/intercom-customer-chatbot.service';
import { CloseButtonComponent } from './shared/components/customer-chatbot/close-button/close-button';
import { PrintServerApi } from '@azavista/planner-lib/lib/onsite-checkin/print-server-api';
import { createSequentialProxy } from './shared/utils';
// AoT requires an exported function for factories

Sentry.init({
    dsn: 'https://f9224615b3f6d29c3c2a3767c053f6d8@o4507837400809472.ingest.de.sentry.io/4507893501788240',
    // Not quite as clean as on the backend, but if we're in the browser it's
    // the best we can do
    environment: window.location.hostname
});

Sentry.configureScope(scope => {
    scope.addEventProcessor(event => {
        event.release = environment.sentryRelease;
        return event;
    });
});

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        SharedModule,
        NavbarModule,
        MatToolbarModule,
        MatSidenavModule,
        MatListModule,
        AppRoutingModule,
        CloseButtonComponent,
    ],
    providers: [
        { provide: EnvironmentService, useClass: environment.isElectron ? ElectronEnvironmentService : WebEnvironmentService },
        {
            provide: MAT_RADIO_DEFAULT_OPTIONS,
            useValue: { color: 'primary' } as MatRadioDefaultOptions,
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { subscriptSizing: 'dynamic', appearance: 'outline' } as MatFormFieldDefaultOptions
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({prefix:'./assets/i18n/', suffix:'.json'}),
        }),
        {
            provide: AuthPersistenceImpl,
            useFactory: () => new AuthPersistenceImpl(),
        },
        ConfigurationStorageService,
        {
            provide: Axios,
            useFactory: (cfgSvc: ConfigurationStorageService, errorSvc: OnsiteCheckInClientErrorService) => createHttpClient(
                cfgSvc.getAppConfig()?.apiServerBaseUrl || environment.apiServerBaseUrl,
                environment.clientId,
                (error) => {
                    return axiosErrorHandler(error, errorSvc);
                }
            ),
            deps: [ ConfigurationStorageService, OnsiteCheckInClientErrorService ],
        },
        {
            provide: AuthServiceImpl,
            useFactory: (persistence: AuthPersistenceImpl, axios: Axios) => {
                const authSvc = new AuthServiceImpl(
                    axios as AxiosInstance,
                    persistence
                );
                return authSvc;
            },
            deps: [ AuthPersistenceImpl, Axios ],
        },
        {
            provide: OnsiteCheckInClient,
            useFactory: (authSvc: AuthServiceImpl) => new OnsiteCheckInClient(authSvc, authSvc['httpClient']),
            deps: [AuthServiceImpl]
        },
        {
            provide: PrintServerApi,
            useFactory: (client: OnsiteCheckInClient) => {
                const configSvc = inject(ConfigurationStorageService);
                client.printServerApi.setPrintServerBaseURL(configSvc.getAppConfig().printServerBaseUrl);
                // we want to ensure that all calls to the print server api are made sequentially to avoid any potential issues with concurrent requests
                return createSequentialProxy(client.printServerApi, ['getPrinterFonts', 'getPrinterStatus', 'calibratePrinter', 'factoryResetPrinter', 'pausePrinter', 'powerOffPrinter', 'print', 'resumePrinter', 'setCutterOff', 'setCutterOn', 'uploadFile']);
            },
            deps: [OnsiteCheckInClient, ConfigurationStorageService],
        },
        {
            provide: CustomerChatbotService,
            useFactory: () => {
                const chatbotSvc = new IntercomCustomerChatbotService();
                chatbotSvc.init('unauthenticated');
                return chatbotSvc;
            }
        },
    ] })
export class AppModule { }
